import { Injectable, Injector } from '@angular/core';
import { AppComponentBase } from '@shared/common/app-component-base';
import { AddressAutocompleteServiceProxy, KeyValuePairOfStringString } from '@shared/service-proxies/service-proxies';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { AppConsts } from '@shared/AppConsts';
import { Observable } from '@node_modules/rxjs/dist/types';

@Injectable()
export class AddressAutoCompleteService extends AppComponentBase {

    streetPropertyName = 'street';
    streetNrPropertyName = 'streetNr';
    postCodePropertyName = 'postCode';
    cityPropertyName = 'city';
    cantonPropertyName = 'cantonId';
    specialSwissAddress = false;

    private forms: Array<{
        postCode: string;
        cities: KeyValuePairOfStringString[];
        citySuggestions: string[];
        streets: KeyValuePairOfStringString[];
        streetSuggestions: string[];
        houseNumbers: KeyValuePairOfStringString[];
        houseNumberSuggestions: string[];
        formReference: FormGroup;
    }> = [];

    constructor(
        private addressAutocompleteServiceProxy: AddressAutocompleteServiceProxy,
        injector: Injector
    ) {
        super(injector);
    }

    private getFormData(index: number = 0) {
        if (!this.forms[index]) {
            this.forms[index] = {
                postCode: null,
                cities: [],
                citySuggestions: [],
                streets: [],
                streetSuggestions: [],
                houseNumbers: [],
                houseNumberSuggestions: [],
                formReference: null
            };
        }
        return this.forms[index];
    }

    setMainFormReference(form: FormGroup, index: number = 0, specialSwissAddress: boolean = false) {
        this.getFormData(index).formReference = form;
        if (specialSwissAddress === true) {
            this.specialSwissAddress = true;
            this.streetPropertyName = 'streetSwiss';
            this.streetNrPropertyName = 'streetNrSwiss';
            this.postCodePropertyName = 'postCodeSwiss';
            this.cityPropertyName = 'citySwiss';
        }
    }

    getMainFormReference(index: number = 0): FormGroup {
        return this.getFormData(index).formReference;
    }

    clearCitySuggestions(index: number = 0) {
        this.getFormData(index).citySuggestions = [];
    }

    clearStreetSuggestions(index: number = 0) {
        this.getFormData(index).streetSuggestions = [];
    }

    clearHouseNumberSuggestions(index: number = 0) {
        this.getFormData(index).houseNumberSuggestions = [];
    }

    public getCitySuggestions(index: number = 0): string[] {
        return this.getFormData(index).citySuggestions;
    }

    public getStreetSuggestions(index: number = 0): string[] {
        return this.getFormData(index).streetSuggestions;
    }

    public getHouseNumberSuggestions(index: number = 0): string[] {
        return this.getFormData(index).houseNumberSuggestions;
    }

    searchCities(postCode: string, index: number = 0, groupName: string = undefined, formArrayIndex: number = 0) {
        const formData = this.getFormData(index);
        if (postCode && postCode.length === 4 && this.isCountrySwitzerland(index, groupName, formArrayIndex)) {
            this.addressAutocompleteServiceProxy.getCitiesByPostCode(postCode).subscribe(cities => {
                formData.citySuggestions = cities.map((city: KeyValuePairOfStringString) => city.value);
                formData.cities = cities;
                formData.postCode = postCode;
            });
        } else {
            formData.citySuggestions = [];
        }
    }

    loadCitiesFromExistingData(postCode: string, city: string, street: string, streetNr: string, index: number = 0) {
        const formData = this.getFormData(index);
        if (postCode.length === 4) {
            this.addressAutocompleteServiceProxy.getCitiesByPostCode(postCode).subscribe(cities => {
                formData.citySuggestions = cities.map((city: KeyValuePairOfStringString) => city.value);
                formData.cities = cities;
                formData.postCode = postCode;

                formData.formReference.get(this.postCodePropertyName).setValue(postCode);
                formData.formReference.get(this.cityPropertyName).setValue(city);

                let cityId = Number(formData.cities.find(c => c.value === city)?.key);
                if (isNaN(cityId)) {
                    return;
                }
                this.addressAutocompleteServiceProxy.getStreetsByCity(cityId, street).subscribe((streets: KeyValuePairOfStringString[]) => {
                    formData.streets = streets;
                    formData.streetSuggestions = streets?.map(x => x.value);
                    formData.formReference.get(this.streetPropertyName).setValue(street);
                });

                if (streetNr != null) {
                    formData.houseNumberSuggestions = [streetNr];
                    formData.formReference.get(this.streetNrPropertyName).setValue(streetNr);
                }
            });
        }
    }

    onCitySelected(city: string, index: number = 0) {
        const formData = this.getFormData(index);
        formData.formReference.get(this.postCodePropertyName).setValue(formData.postCode);
        formData.formReference.get(this.cityPropertyName).setValue(city);
        formData.postCode = null;
        formData.citySuggestions = [];
        if (formData.formReference.contains(this.cantonPropertyName)){
            let postCode = formData.formReference.get(this.postCodePropertyName).getRawValue();
            if (postCode?.length === 4) {
                this.addressAutocompleteServiceProxy.getCantonByPostCode(postCode).subscribe(canton => {
                    formData.formReference.get(this.cantonPropertyName).setValue(canton);
                });
            }
        }
    }

    onCitySelectedKeepingSuggestions(city: string, index: number = 0) {
        const formData = this.getFormData(index);
        formData.formReference.get(this.postCodePropertyName).setValue(formData.postCode);
        formData.formReference.get(this.cityPropertyName).setValue(city);
        formData.postCode = null;
    }

    onCitySelectedForIndex(city: string, index: number, groupName: string, formArrayIndex: number = 0) {
        const formData = this.getFormData(index);
        const previousEmployments = formData.formReference.get(groupName) as FormArray;
        const previousemployment = previousEmployments.at(formArrayIndex) as FormGroup;
        previousemployment.get(this.postCodePropertyName).setValue(formData.postCode);
        previousemployment.get(this.cityPropertyName).setValue(city);
        formData.postCode = null;
        formData.citySuggestions = [];
    }

    searchStreets(query: string, index: number = 0, groupName: string = undefined, formArrayIndex: number = 0) {
        const formData = this.getFormData(index);
        let city = '';
        if (groupName !== undefined) {
            const previousEmployments = formData.formReference.get(groupName) as FormArray;
            const previousemployment = previousEmployments.at(formArrayIndex) as FormGroup;
            city = previousemployment.get(this.cityPropertyName).value;
        } else {
            city = formData.formReference.get(this.cityPropertyName).value;
        }

        let cityId = Number(formData.cities.find(c => c.value === city)?.key);
        if (isNaN(cityId)) {
            formData.streets = [];
            formData.streetSuggestions = [];
            return;
        }

        this.addressAutocompleteServiceProxy.getStreetsByCity(cityId, query).subscribe((streets: KeyValuePairOfStringString[]) => {
            formData.streets = streets;
            formData.streetSuggestions = streets.map(x => x.value);
        });
    }

    searchStreetsByCity(city: string, query: string = 'A', index: number = 0): void {
        const formData = this.getFormData(index);
        let cityId = Number(formData.cities.find(c => c.value === city)?.key);
        this.addressAutocompleteServiceProxy.getStreetsByCity(cityId, query).subscribe((streets: KeyValuePairOfStringString[]) => {
            formData.streets = streets;
            formData.streetSuggestions = streets?.map(x => x.value);
        });
    }

    onStreetSelected(selection: string, index: number = 0) {
        const formData = this.getFormData(index);
    }

    searchHouseNumbers(query: string = '1', index: number = 0, groupName: string = undefined, formArrayIndex: number = 0) {
        const formData = this.getFormData(index);
        let street = '';
        if (groupName !== undefined) {
            const previousEmployments = formData.formReference.get(groupName) as FormArray;
            const previousemployment = previousEmployments.at(formArrayIndex) as FormGroup;
            street = previousemployment.get(this.streetPropertyName).value;
        } else {
            street = formData.formReference.get(this.streetPropertyName).value;
        }

        let streetId = Number(formData.streets.find(str => str.value === street)?.key);
        if (isNaN(streetId)) {
            formData.houseNumbers = [];
            formData.houseNumberSuggestions = [];
            return;
        }

        this.addressAutocompleteServiceProxy.getHouseNumbersByStreetId(streetId, query).subscribe((houseNumbers: KeyValuePairOfStringString[]) => {
            formData.houseNumbers = houseNumbers;
            formData.houseNumberSuggestions = houseNumbers.map(x => x.value);
        });
    }

    searchHouseNumbersByStreet(street: string, query: string = '1', index: number = 0, groupName: string = undefined) {
        const formData = this.getFormData(index);
        let streetId = Number(formData.streets.find(str => str.value === street)?.key);
        if (isNaN(streetId)) {
            formData.houseNumbers = [];
            formData.houseNumberSuggestions = [];
            return;
        }

        this.addressAutocompleteServiceProxy.getHouseNumbersByStreetId(streetId, query).subscribe((houseNumbers: KeyValuePairOfStringString[]) => {
            formData.houseNumbers = houseNumbers;
            formData.houseNumberSuggestions = houseNumbers.map(x => x.value);
        });
    }

    onHouseNumberSelected(selection: KeyValuePairOfStringString, index: number = 0) {
        const formData = this.getFormData(index);
    }

    isCountrySwitzerland(index: number = 0, groupName: string = undefined, formArrayIndex: number = 0): boolean {
        if (this.specialSwissAddress === true) {
            return true; // If special Swiss address is enabled, we assume it's always Switzerland
        }

        const formData = this.getFormData(index);
        let countryControl: AbstractControl;
        if (groupName !== undefined) {
            const previousEmployments = formData.formReference.get(groupName) as FormArray;
            const previousemployment = previousEmployments.at(formArrayIndex) as FormGroup;
            countryControl = previousemployment.get('countryId');
        } else {
            countryControl = formData.formReference.get('countryId');
        }

        let isCountryCh = true;
        if (countryControl != null) { // The country field is not required
            isCountryCh = countryControl.value === AppConsts.codes.country.ch;
        }

        return isCountryCh;
    }

    isPostCodeInCanton(postCode: string | undefined, cantonAbbreviation: string | undefined): Observable<boolean> {
        return this.addressAutocompleteServiceProxy.isPostCodeInCanton(postCode, cantonAbbreviation);
    }
}
